import * as XLSX from "xlsx";
import JSZip from "jszip";

// A build "community" da lib xlsx (SheetJS) não grava estilo de célula
// (fill/cor) nem data validation nativa do Excel ao salvar — só a versão
// paga faz isso. Por isso:
//  - a proteção das colunas travadas é feita por convenção visual (prefixo
//    🔒 no cabeçalho + aviso na primeira linha) e por validação em código
//    (aqui no client, antes do envio, e de novo no backend ao importar);
//  - os dropdowns de Coluna CRM / Status CRM / Setor são conseguidos
//    injetando manualmente o XML de <dataValidations> no .xlsx depois de
//    gerado (ver injectDataValidations), já que a API pública da lib
//    ignora `!dataValidation` ao escrever.
export const LOCKED_PREFIX = "🔒 ";

export const HEADERS = [
  `${LOCKED_PREFIX}Nome`,
  `${LOCKED_PREFIX}Número`,
  `${LOCKED_PREFIX}País`,
  `${LOCKED_PREFIX}E-mail`,
  `${LOCKED_PREFIX}Carteira`,
  `${LOCKED_PREFIX}Observações`,
  "Coluna CRM",
  "Status CRM",
  "Valor do Negócio",
  "Data de Início",
  "Data limite",
  "Setor",
];

// Índices (0-based) das colunas editáveis que recebem dropdown.
const COL_LANE = 6;
const COL_STATUS = 7;
const COL_QUEUE = 11;

export const WARNING_TEXT =
  "⚠️ NÃO altere as colunas com o cadeado 🔒 (Nome, Número, País, E-mail, Carteira, Observações) — " +
  "elas identificam o contato no sistema (o Número é a chave). Qualquer alteração nelas faz a linha ser rejeitada na importação. " +
  "Coluna CRM, Status CRM e Setor têm uma lista de opções válidas (clique na célula) — a aba \"Listas\" mostra todas elas.";

export const CRM_STATUS_OPTIONS = ["Não iniciado", "Contato", "Em andamento", "Proposta", "Follow up", "Sucesso", "Perdido"];

const colLetter = (i) => XLSX.utils.encode_col(i);

/**
 * Injeta <dataValidations> no XML de uma planilha específica de um .xlsx
 * já gerado pela lib (que ignora essa propriedade ao escrever). Cada
 * validação é do tipo "list" apontando para um range de células de outra
 * aba (não uma lista inline: nomes podem ter espaços/acentos, e a fórmula
 * inline tem limite de 255 caracteres).
 *
 * `validations`: [{ colIndex, sheetName, fromRow, toRow, listSheetName, listRange }]
 */
async function injectDataValidations(arrayBuffer, sheetFileName, validations, maxDataRow) {
  const zip = await JSZip.loadAsync(arrayBuffer);
  const path = `xl/worksheets/${sheetFileName}`;
  const file = zip.file(path);
  if (!file) return arrayBuffer; // por segurança, se algo não bater, devolve o original sem quebrar o download

  let xml = await file.async("string");

  const dvEntries = validations
    .map(v => {
      const sqref = `${colLetter(v.colIndex)}${v.fromRow}:${colLetter(v.colIndex)}${maxDataRow}`;
      return (
        `<dataValidation type="list" allowBlank="1" showInputMessage="1" showErrorMessage="1" ` +
        `errorTitle="Valor inválido" error="Selecione um valor da lista." sqref="${sqref}">` +
        `<formula1>${v.listRange}</formula1></dataValidation>`
      );
    })
    .join("");

  const dvXml = `<dataValidations count="${validations.length}">${dvEntries}</dataValidations>`;

  // A ordem dos elementos filhos de <worksheet> é fixa no schema OOXML:
  // dataValidations vem depois de sheetData/mergeCells e antes de
  // hyperlinks/pageMargins/ignoredErrors. Insere antes do primeiro desses
  // marcadores que existir no XML, ou antes de </worksheet> como fallback.
  const anchors = ["<hyperlinks", "<pageMargins", "<ignoredErrors", "</worksheet>"];
  let inserted = false;
  let newXml = xml;
  for (const anchor of anchors) {
    const idx = xml.indexOf(anchor);
    if (idx !== -1) {
      newXml = xml.slice(0, idx) + dvXml + xml.slice(idx);
      inserted = true;
      break;
    }
  }
  if (!inserted) return arrayBuffer;

  zip.file(path, newXml);
  // "array" no JSZip devolve um Array comum de números (não um
  // ArrayBuffer/TypedArray) — passado direto pro Blob, cada byte é
  // serializado como texto decimal em vez de byte binário, corrompendo o
  // arquivo (Excel recusa com "formato ou extensão de arquivo não é
  // válida"). "uint8array" devolve o binário real.
  return zip.generateAsync({ type: "uint8array" });
}

const triggerDownload = (arrayBuffer, fileName) => {
  const blob = new Blob([arrayBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Monta e baixa o .xlsx do template a partir das linhas devolvidas por
 * GET /tickets/crm-template-data — uma por card ativo do CRM (não por
 * contato cadastrado: contatos sem card não entram no template).
 *
 * `laneNames` e `queueNames`: nomes das colunas do kanban e dos setores
 * cadastrados na empresa, usados para popular os dropdowns de Coluna CRM
 * e Setor (Status CRM usa a lista fixa CRM_STATUS_OPTIONS).
 */
export const downloadCrmTemplate = async (rows, { laneNames = [], queueNames = [] } = {}) => {
  // Remove vazios/duplicados — evita linhas estranhas na lista e ranges
  // maiores do que precisam ser.
  laneNames = Array.from(new Set(laneNames.map(n => (n || "").trim()).filter(Boolean)));
  queueNames = Array.from(new Set(queueNames.map(n => (n || "").trim()).filter(Boolean)));

  const aoa = [
    [WARNING_TEXT],
    HEADERS,
    ...rows.map(r => [
      r.name,
      r.number,
      r.country,
      r.email,
      r.carteira,
      r.observacoes,
      r.laneName || "",
      r.crmStatus || "",
      r.valorVenda != null ? r.valorVenda : "",
      r.dataInicio || "",
      r.dataFim || "",
      r.queueName || "",
    ]),
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: HEADERS.length - 1 } }];
  ws["!cols"] = HEADERS.map((h, i) => ({
    wch: i === 5 ? 40 : Math.max(h.length + 2, 14),
  }));

  // Aba auxiliar com as opções válidas do sistema — é para onde os
  // dropdowns apontam. Fica oculta: quem abre o arquivo só vê a aba "CRM",
  // mas o dropdown funciona normalmente.
  const listColumns = [
    { header: "Colunas do CRM disponíveis", values: laneNames },
    { header: "Status CRM disponíveis", values: CRM_STATUS_OPTIONS },
    { header: "Setores disponíveis", values: queueNames },
  ];
  const maxListLen = Math.max(...listColumns.map(c => c.values.length), 0);
  const listAoa = [
    listColumns.map(c => c.header),
    ...Array.from({ length: maxListLen }, (_, i) =>
      listColumns.map(c => c.values[i] || "")
    ),
  ];
  const listWs = XLSX.utils.aoa_to_sheet(listAoa);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "CRM");
  XLSX.utils.book_append_sheet(wb, listWs, "Listas");
  // Esconde a aba de listas (índice 1, a segunda adicionada) — quem abre
  // o arquivo só vê a aba "CRM", mas os dropdowns continuam funcionando.
  wb.Workbook = { Sheets: [{}, { Hidden: 1 }] };

  const now = new Date();
  const pad = n => String(n).padStart(2, "0");
  const suffix = `${pad(now.getDate())}_${pad(now.getMonth() + 1)}_${now.getFullYear()}_${pad(now.getHours())}_${pad(now.getMinutes())}`;
  const fileName = `Template_CRM_${suffix}.xlsx`;

  let arrayBuffer = XLSX.write(wb, { type: "array", bookType: "xlsx" });

  // Linha de dados vai de 3 (após aviso + cabeçalho) até a última linha
  // preenchida + uma margem para o usuário adicionar poucas linhas extras
  // (o import é por número, então uma linha extra em branco é inofensiva).
  const maxDataRow = rows.length + 2 + 20;
  try {
    arrayBuffer = await injectDataValidations(
      arrayBuffer,
      "sheet1.xml", // "CRM" é a 1ª aba adicionada -> sheet1.xml
      [
        laneNames.length > 0 && {
          colIndex: COL_LANE,
          fromRow: 3,
          listRange: `Listas!$A$2:$A$${maxListLen + 1}`,
        },
        { colIndex: COL_STATUS, fromRow: 3, listRange: `Listas!$B$2:$B$${CRM_STATUS_OPTIONS.length + 1}` },
        queueNames.length > 0 && {
          colIndex: COL_QUEUE,
          fromRow: 3,
          listRange: `Listas!$C$2:$C$${maxListLen + 1}`,
        },
      ].filter(Boolean),
      maxDataRow
    );
  } catch (err) {
    // Falha ao injetar o dropdown não pode impedir o download do template
    // sem os dropdowns — a validação em código continua funcionando.
    console.error("Falha ao aplicar dropdowns no template do CRM:", err);
  }

  triggerDownload(arrayBuffer, fileName);
};

// dd/MM/yyyy ou yyyy-MM-dd. Vazio é sempre válido (campo opcional).
const isValidDateCell = (v) => {
  if (!v) return true;
  return /^\d{4}-\d{2}-\d{2}$/.test(v) || /^\d{2}\/\d{2}\/\d{4}$/.test(v);
};

/**
 * Valida uma linha já lida do arquivo (mesmas regras aplicadas de novo no
 * backend — esta camada só existe para o usuário ver o problema na hora,
 * sem esperar o roundtrip do servidor). Devolve a lista de mensagens de
 * erro; vazia = linha ok.
 */
export const validateCrmTemplateRow = (row, { laneNames, queueNames }) => {
  const errors = [];

  if (!row.name.trim()) errors.push("Nome vazio");
  if (!row.number.replace(/\D/g, "")) errors.push("Número vazio ou inválido");

  if (row.crmStatus && !CRM_STATUS_OPTIONS.includes(row.crmStatus)) {
    errors.push(`Status CRM inválido ("${row.crmStatus}")`);
  }

  if (row.valorVenda) {
    const normalized = row.valorVenda.replace(",", ".");
    if (Number.isNaN(Number(normalized))) {
      errors.push(`Valor do Negócio inválido ("${row.valorVenda}")`);
    }
  }

  if (!isValidDateCell(row.dataInicio)) {
    errors.push(`Data de Início inválida ("${row.dataInicio}")`);
  }
  if (!isValidDateCell(row.dataFim)) {
    errors.push(`Data limite inválida ("${row.dataFim}")`);
  }

  if (row.laneName && !laneNames.has(row.laneName.trim().toLowerCase())) {
    errors.push(`Coluna CRM não encontrada ("${row.laneName}")`);
  }
  if (row.queueName && !queueNames.has(row.queueName.trim().toLowerCase())) {
    errors.push(`Setor não encontrado ("${row.queueName}")`);
  }

  return errors;
};

/**
 * Lê um .xlsx no formato do template (pulando a linha de aviso) e devolve
 * as linhas já normalizadas para o payload de importação.
 */
export const parseCrmTemplateFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        // range: 1 pula a linha de aviso, header:1 usa a 2ª linha (índice 0
        // após o range) como array de valores em vez de inferir chaves.
        const matrix = XLSX.utils.sheet_to_json(ws, {
          header: 1,
          range: 1,
          defval: "",
          raw: false,
        });

        const [headerRow, ...dataRows] = matrix;
        if (!headerRow || headerRow.length < HEADERS.length) {
          reject(new Error("Arquivo não parece ser o template do CRM (cabeçalho inesperado)."));
          return;
        }

        const rows = dataRows
          .filter(r => r.some(cell => String(cell).trim() !== ""))
          .map(r => ({
            name: String(r[0] || "").trim(),
            number: String(r[1] || "").trim(),
            country: String(r[2] || "").trim(),
            email: String(r[3] || "").trim(),
            carteira: String(r[4] || "").trim(),
            observacoes: String(r[5] || "").trim(),
            laneName: String(r[6] || "").trim(),
            crmStatus: String(r[7] || "").trim(),
            valorVenda: String(r[8] || "").trim(),
            dataInicio: String(r[9] || "").trim(),
            dataFim: String(r[10] || "").trim(),
            queueName: String(r[11] || "").trim(),
          }));

        resolve(rows);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsBinaryString(file);
  });
};
